import React from 'react'

import { COUNT_UP_DURATION_MS, COUNT_UP_EASING } from '../lib/summaryConstants'

export type CountUpMode = 'amount' | 'count'

export { COUNT_UP_DURATION_MS } from '../lib/summaryConstants'

const MAX_DISCRETE_STEPS = 200

const createCubicBezierEase = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): ((t: number) => number) => {
  const ax = 3 * x1 - 3 * x2 + 1
  const bx = 3 * x2 - 6 * x1
  const cx = 3 * x1
  const ay = 3 * y1 - 3 * y2 + 1
  const by = 3 * y2 - 6 * y1
  const cy = 3 * y1

  const sampleCurveX = (t: number) => ((ax * t + bx) * t + cx) * t
  const sampleCurveY = (t: number) => ((ay * t + by) * t + cy) * t
  const sampleCurveDerivativeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx

  const solveCurveX = (x: number) => {
    let t2 = x

    for (let i = 0; i < 8; i += 1) {
      const slope = sampleCurveDerivativeX(t2)
      if (Math.abs(slope) < 1e-6) break
      const delta = sampleCurveX(t2) - x
      t2 -= delta / slope
    }

    let t0 = 0
    let t1 = 1

    while (t0 < t1) {
      const x2 = sampleCurveX(t2)
      if (Math.abs(x2 - x) < 1e-7) return t2
      if (x > x2) t0 = t2
      else t1 = t2
      t2 = (t0 + t1) / 2
    }

    return t2
  }

  return (t: number) => {
    if (t <= 0) return 0
    if (t >= 1) return 1
    return sampleCurveY(solveCurveX(t))
  }
}

export const countUpEase = createCubicBezierEase(...COUNT_UP_EASING)

export const countUpUnits = (
  from: number,
  to: number,
  mode: CountUpMode,
): { startUnits: number; endUnits: number; totalSteps: number } => {
  const startUnits = mode === 'amount' ? Math.round(from * 100) : Math.round(from)
  const endUnits = mode === 'amount' ? Math.round(to * 100) : Math.round(to)
  const rawSteps = Math.abs(endUnits - startUnits)
  const totalSteps = Math.min(rawSteps, MAX_DISCRETE_STEPS)
  return { startUnits, endUnits, totalSteps }
}

export const countUpDisplayAtProgress = (
  from: number,
  to: number,
  mode: CountUpMode,
  easedProgress: number,
): number => {
  const { startUnits, endUnits, totalSteps } = countUpUnits(from, to, mode)
  if (totalSteps === 0) return to

  const rawSteps = Math.abs(endUnits - startUnits)
  const currentStep = Math.round(easedProgress * totalSteps)
  const stepSize = rawSteps / totalSteps
  const currentUnits = startUnits + Math.sign(endUnits - startUnits) * Math.round(currentStep * stepSize)

  if (mode === 'amount') {
    return currentUnits / 100
  }
  return currentUnits
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

type Options = {
  from: number
  to: number
  mode: CountUpMode
  duration?: number
  enabled?: boolean
}

type Result = {
  displayValue: number
  isAnimating: boolean
  justFinished: boolean
}

export const useCountUp = ({
  from,
  to,
  mode,
  duration = COUNT_UP_DURATION_MS,
  enabled = true,
}: Options): Result => {
  const getInitialValue = () => {
    if (!enabled || from === to) return to
    if (prefersReducedMotion()) return to
    return from
  }

  const [displayValue, setDisplayValue] = React.useState(getInitialValue)
  const [isAnimating, setIsAnimating] = React.useState(false)
  const [justFinished, setJustFinished] = React.useState(false)

  React.useEffect(() => {
    setJustFinished(false)

    if (!enabled || from === to) {
      setDisplayValue(to)
      setIsAnimating(false)
      return
    }

    if (prefersReducedMotion()) {
      setDisplayValue(to)
      setIsAnimating(false)
      return
    }

    setIsAnimating(true)
    setDisplayValue(from)

    const startTime = performance.now()
    let rafId = 0

    const tick = (now: number) => {
      const elapsed = now - startTime
      const t = Math.min(elapsed / duration, 1)
      const eased = countUpEase(t)
      const nextValue = countUpDisplayAtProgress(from, to, mode, eased)
      setDisplayValue(nextValue)

      if (t < 1) {
        rafId = requestAnimationFrame(tick)
        return
      }

      setDisplayValue(to)
      setIsAnimating(false)
      setJustFinished(true)
    }

    rafId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [from, to, mode, duration, enabled])

  return { displayValue, isAnimating, justFinished }
}
